/**
 * CountsStrip — Phase R10.3 (2026-07-04)
 *
 * 8-card dashboard strip rendering the status + flag + eligibility
 * counts from GET /api/owner/receipt-claims/counts. Each card is
 * clickable; clicking filters the list below by the matching
 * status/flag via the parent-provided onCardClick callback.
 */
import {
  Clock, Hourglass, CheckCircle2, XCircle,
  AlertOctagon, AlertTriangle, Zap, UserCheck,
} from 'lucide-react';

const CARDS = [
  { key: 'pending_owner_review', label: 'Pending Review',       helper: 'Awaiting your decision',    Icon: Clock,         cls: 'text-amber-300 border-amber-500/30' },
  { key: 'pending_ai_analysis',  label: 'Analyzing',            helper: 'AI reading receipts',       Icon: Hourglass,     cls: 'text-white/70  border-white/15'      },
  { key: 'approved',             label: 'Approved',             helper: 'Stamps awarded',            Icon: CheckCircle2,  cls: 'text-emerald-300 border-emerald-500/30' },
  { key: 'rejected',             label: 'Rejected',             helper: 'Decline + confirmed dupe',  Icon: XCircle,       cls: 'text-red-300 border-red-500/30' },
  { key: 'flag_red',             label: 'Confirmed Duplicate',  helper: 'Hard-blocked',              Icon: AlertOctagon,  cls: 'text-red-300 border-red-600/40' },
  { key: 'flag_orange_yellow',   label: 'Potential Duplicate',  helper: 'Review before approve',     Icon: AlertTriangle, cls: 'text-orange-300 border-orange-500/30' },
  { key: 'eligible_for_auto_approval', label: 'Eligible for Auto-Approval', helper: 'Clean + confident', Icon: Zap,           cls: 'text-emerald-300 border-emerald-500/40' },
  { key: 'manual_review_required',     label: 'Manual Review Required',     helper: 'Need your eyes',   Icon: UserCheck,     cls: 'text-amber-300 border-amber-500/30' },
];

export default function CountsStrip({ counts, isLoading, isError, onCardClick }) {
  const resolveValue = (key) => {
    if (!counts) return null;
    if (key === 'flag_orange_yellow') {
      return (counts.flag_orange ?? 0) + (counts.flag_yellow ?? 0);
    }
    return counts[key] ?? 0;
  };

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2"
      data-testid="counts-strip"
    >
      {CARDS.map((c) => {
        const val = resolveValue(c.key);
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onCardClick?.(c.key)}
            data-testid={`counts-card-${c.key}`}
            className={
              'flex flex-col items-start text-left p-3 rounded-2xl border bg-[#15151B] hover:bg-white/[0.03] transition-colors ' +
              c.cls
            }
          >
            <div className="flex items-center gap-1.5 mb-1">
              <c.Icon size={12} />
              <span className="text-[10px] uppercase tracking-wider opacity-70">{c.label}</span>
            </div>
            <div className="text-3xl font-bold text-white leading-none">
              {isLoading ? (
                <span className="inline-block w-10 h-6 rounded bg-white/5 animate-pulse" />
              ) : isError ? (
                <span className="text-white/40">—</span>
              ) : (
                val
              )}
            </div>
            <div className="text-[11px] text-white/40 mt-1">{c.helper}</div>
          </button>
        );
      })}
    </div>
  );
}
